import { Deviation_Survey_Directional_Dataset_Enum } from "gql/api";
import type { DeviationSurveyEntryRows } from "@/composables/scenarioCalculations/calculationTypes";
import {
  inclinationBetweenPointsDeg,
  getMeasuredDepthFor3DFt,
  getMeasuredDepthFor2DFt,
  getDogLegSeverityDeg100Ft,
  getTrueVerticalDepthFor3DFt,
  getTrueVerticalDepthFor2DFt
} from "./geometry";

export function extrapolateEntryRowAtTrueVerticalDepth(
  trueVerticalDepth: number,
  previousEntryRow: number[],
  nextEntryRow: number[],
  surveyType: Deviation_Survey_Directional_Dataset_Enum | null | undefined
): number[] {
  const [prevMD, prevIncl, prevAz, prevTVD] = previousEntryRow;
  const [nextMD, , nextAz, nextTVD] = nextEntryRow;

  // If the trueVerticalDepth matches exactly with either the previous or next row, return that row
  if (trueVerticalDepth === prevTVD) {
    return previousEntryRow;
  } else if (trueVerticalDepth === nextTVD) {
    return nextEntryRow;
  }

  const azimuthDeg = (prevAz + nextAz) / 2;

  // Calculate the inclination angle between the two points
  const inclinationAngleDeg = inclinationBetweenPointsDeg(
    prevMD,
    nextMD,
    prevTVD,
    nextTVD
  );

  // Choose the measured depth calculation based on survey type
  const measuredDepthFt =
    surveyType ===
    Deviation_Survey_Directional_Dataset_Enum.ThreeDMdInclinationAzimuth
      ? getMeasuredDepthFor3DFt(
          trueVerticalDepth,
          prevTVD,
          inclinationAngleDeg,
          prevIncl,
          prevMD,
          azimuthDeg,
          prevAz
        )
      : getMeasuredDepthFor2DFt(
          trueVerticalDepth,
          prevTVD,
          inclinationAngleDeg,
          prevMD
        );

  const dogLegSeverityDeg100Ft = getDogLegSeverityDeg100Ft(
    prevMD,
    measuredDepthFt,
    prevIncl,
    inclinationAngleDeg,
    prevAz,
    azimuthDeg
  );

  return [
    measuredDepthFt,
    inclinationAngleDeg,
    azimuthDeg,
    trueVerticalDepth,
    dogLegSeverityDeg100Ft
  ];
}

//
// This function should use a given measured depth and two entry rows
// to extrapolate a new entry row for the deviation survey
//
export function extrapolateEntryRowAtMeasuredDepth(
  measuredDepth: number,
  previousEntryRow: number[],
  nextEntryRow: number[],
  surveyType: Deviation_Survey_Directional_Dataset_Enum | null | undefined
): number[] {
  const [prevMD, prevIncl, prevAz, prevTVD] = previousEntryRow;
  const nextMD = nextEntryRow[0];
  const nextAz = nextEntryRow[2];
  const nextTVD = nextEntryRow[3];

  // If the measuredDepth matches exactly with either the previous or next row, return that row
  if (measuredDepth === prevMD) {
    return previousEntryRow;
  } else if (measuredDepth === nextMD) {
    return nextEntryRow;
  }

  const azimuthDeg = (prevAz + nextAz) / 2;

  // Calculate the inclination angle between the two points
  const inclinationAngleDeg = inclinationBetweenPointsDeg(
    prevMD,
    nextMD,
    prevTVD,
    nextTVD
  );

  // Choose the TVD calculation based on survey type
  const trueVerticalDepthFt =
    surveyType ===
    Deviation_Survey_Directional_Dataset_Enum.ThreeDMdInclinationAzimuth
      ? getTrueVerticalDepthFor3DFt(
          measuredDepth,
          prevMD,
          inclinationAngleDeg,
          prevIncl,
          prevTVD,
          azimuthDeg,
          prevAz
        )
      : getTrueVerticalDepthFor2DFt(
          measuredDepth,
          prevMD,
          inclinationAngleDeg,
          prevTVD
        );

  const dogLegSeverityDeg100Ft = getDogLegSeverityDeg100Ft(
    prevMD,
    measuredDepth,
    prevIncl,
    inclinationAngleDeg,
    prevAz,
    azimuthDeg
  );

  return [
    measuredDepth,
    inclinationAngleDeg,
    azimuthDeg,
    trueVerticalDepthFt,
    dogLegSeverityDeg100Ft
  ];
}

export function extrapolateMDByTVD(
  totalVerticalDepthFt: number,
  entryRows: DeviationSurveyEntryRows
): number {
  if (entryRows.surveyType) {
    const rowIndex = entryRows.data.findIndex(
      row => row[3] === totalVerticalDepthFt
    );
    let md;
    if (rowIndex !== -1) {
      // Exact match found
      md = entryRows.data[rowIndex][0];
    } else {
      // No exact match, find the closest rows and extrapolate
      const [prevRow, nextRow] = findClosestRowsByTVD(
        totalVerticalDepthFt,
        entryRows.data
      );
      const extrapolatedRow = extrapolateEntryRowAtTrueVerticalDepth(
        totalVerticalDepthFt,
        prevRow,
        nextRow,
        entryRows.surveyType
      );
      md = extrapolatedRow[0];
    }
    return md;
  } else {
    const md = calculateMDWithoutSurvey(entryRows.data, totalVerticalDepthFt);
    return md;
  }
}

export const getClosestSurveyPointByMD = (
  entryRows: number[][],
  md: number
): number[] => {
  const idx = entryRows.findIndex(row => row[0] === md);
  if (idx > -1) {
    return entryRows[idx];
  } else {
    // find closest tvd
    const closestRowByMD = entryRows.reduce((prev, curr) =>
      Math.abs(curr[0] - md) < Math.abs(prev[0] - md) ? curr : prev
    );
    return closestRowByMD;
  }
};

export function extrapolateTVDByMD(
  measuredDepthFt: number,
  entryRows: DeviationSurveyEntryRows
): number {
  if (entryRows.surveyType) {
    const rowIndex = entryRows.data.findIndex(
      row => row[0] === measuredDepthFt
    );
    let tvd;
    if (rowIndex !== -1) {
      // Exact match found
      tvd = entryRows.data[rowIndex][3];
    } else {
      let prevRow: number[];
      let nextRow: number[];
      const lastSurveyRowMD = entryRows.data[entryRows.data.length - 1][0];
      // measuredDepthFt is within the range of the entry rows
      if (measuredDepthFt < lastSurveyRowMD) {
        // No exact match, find the closest rows and extrapolate
        [prevRow, nextRow] = findClosestRowsByMD(
          measuredDepthFt,
          entryRows.data
        );
      } else {
        // Since all surveys start with 0 MD, the only way we get here
        // is if the measuredDepthFt is greater than the last MD in the entry row
        // Set rows to the last two rows in the survey
        prevRow = entryRows.data[entryRows.data.length - 2];
        nextRow = entryRows.data[entryRows.data.length - 1];
      }

      const extrapolatedRow = extrapolateEntryRowAtMeasuredDepth(
        measuredDepthFt,
        prevRow,
        nextRow,
        entryRows.surveyType
      );
      tvd = extrapolatedRow[3];
    }
    return tvd;
  } else {
    const tvd = calculateTVDWithoutSurvey(entryRows.data, measuredDepthFt);
    return tvd;
  }
}

export function calculateMDWithoutSurvey(
  entryData: number[][],
  trueVerticalDepthFt: number
): number {
  // Extract the known TVD and MD from entryData. Top of perfs values
  // are the only values in entryData if this is being called
  const [, , , topOfPerfsTVDFt] = entryData[1];
  const [topOfPerfsMDFt] = entryData[1];
  const CAH = Math.acos(topOfPerfsTVDFt / topOfPerfsMDFt);
  if (CAH === 0) {
    return trueVerticalDepthFt;
  }
  const measuredDepthFt = trueVerticalDepthFt / Math.cos(CAH);
  return measuredDepthFt;
}

export function calculateTVDWithoutSurvey(
  entryData: number[][],
  measuredDepthFt: number
): number {
  // Extract the known TVD and MD from entryData. Top of perfs values
  // are the only values in entryData if this is being called
  const [, , , topOfPerfsTVDFt] = entryData[1];
  const [topOfPerfsMDFt] = entryData[1];
  const CAH = Math.acos(topOfPerfsTVDFt / topOfPerfsMDFt);
  if (CAH === 0) {
    return measuredDepthFt;
  }
  const trueVerticalDepthFt = Math.cos(CAH) * measuredDepthFt;
  return trueVerticalDepthFt;
}

export function findClosestRowsByTVD(
  trueVerticalDepth: number,
  entryData: number[][]
): [number[], number[]] {
  const tvdIndex = 3; // TVD value is at index 3
  let prevRow: number[] = entryData[0];
  let nextRow: number[] = entryData[entryData.length - 1];
  for (let i = 0; i < entryData.length; i++) {
    const row = entryData[i];
    if (row[tvdIndex] <= trueVerticalDepth) {
      prevRow = row;
    } else {
      nextRow = row;
      break;
    }
  }
  // Handle edge cases where trueVerticalDepth is beyond the available data range
  if (trueVerticalDepth <= entryData[0][tvdIndex]) {
    // If trueVerticalDepth is less than the first row, use the first row as both prevRow and nextRow
    prevRow = nextRow = entryData[0];
  } else if (trueVerticalDepth >= entryData[entryData.length - 1][tvdIndex]) {
    // If trueVerticalDepth is greater than the last row, use the last row as both prevRow and nextRow
    prevRow = nextRow = entryData[entryData.length - 1];
  }
  return [prevRow, nextRow];
}

export function findClosestRowsByMD(
  measuredDepth: number,
  entryData: number[][]
): [number[], number[]] {
  let prevRow: number[] = entryData[0];
  let nextRow: number[] = entryData[entryData.length - 1];
  for (let i = 0; i < entryData.length; i++) {
    const row = entryData[i];
    if (row[0] <= measuredDepth) {
      prevRow = row;
    } else {
      nextRow = row;
      break;
    }
  }
  // Handle edge cases where measuredDepth is beyond the available data range
  if (measuredDepth <= entryData[0][0]) {
    // If measuredDepth is less than the first row, use the first row as both prevRow and nextRow
    prevRow = nextRow = entryData[0];
  } else if (measuredDepth >= entryData[entryData.length - 1][0]) {
    // If measuredDepth is greater than the last row, use the last row as both prevRow and nextRow
    prevRow = nextRow = entryData[entryData.length - 1];
  }
  return [prevRow, nextRow];
}
